import { cn } from '@repo/utils';

type InlineAlertVariant = 'danger' | 'warning' | 'info';

export interface InlineAlertFailedField {
  /** Matches the field's `htmlFor` so the anchor link scrolls/focuses it. */
  htmlFor: string;
  label: string;
}

interface InlineAlertProps {
  variant?: InlineAlertVariant;
  /** Failed fields, each rendered as an anchor link to `#{htmlFor}`. */
  failedFields?: InlineAlertFailedField[];
  /** Non-field-specific issues (e.g. unmapped server errors) — rendered as plain list items. */
  formErrors?: string[];
  title?: string;
  className?: string;
}

const variantClasses: Record<InlineAlertVariant, { border: string; badge: string; text: string }> = {
  danger: {
    border: 'border-danger-500/30',
    badge: 'bg-danger-bg text-danger-text',
    text: 'text-danger-text',
  },
  warning: {
    border: 'border-warning-500/30',
    badge: 'bg-warning-bg text-warning-text',
    text: 'text-warning-text',
  },
  info: {
    border: 'border-info-500/30',
    badge: 'bg-info-bg text-info-text',
    text: 'text-info-text',
  },
};

/**
 * Form-level summary of failed fields (reference/UI-GOVERNANCE.md §6.3) —
 * renders alongside each field's own inline message, never replacing it.
 * Distinct from `Banner` (page-level standing conditions) and from a per-field
 * `FormField` error (which uses `aria-live="polite"`): this is a post-submit
 * interrupt, so it's `aria-live="assertive"`.
 */
export function InlineAlert({
  variant = 'danger',
  failedFields = [],
  formErrors = [],
  title,
  className,
}: InlineAlertProps) {
  if (failedFields.length === 0 && formErrors.length === 0) return null;

  const styles = variantClasses[variant];
  const heading = title ?? (failedFields.length > 0 ? 'Please fix the following' : 'Something went wrong');

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn('rounded-lg border bg-surface-1 p-4', styles.border, className)}
    >
      <p className={cn('text-sm font-semibold inline-block px-2 py-0.5 rounded-sm mb-2', styles.badge)}>
        {heading}
      </p>
      <ul className={cn('text-sm space-y-1 list-disc list-inside', styles.text)}>
        {failedFields.map((field) => (
          <li key={field.htmlFor}>
            <a href={`#${field.htmlFor}`} className="underline underline-offset-2">
              {field.label}
            </a>
          </li>
        ))}
        {formErrors.map((message, index) => (
          <li key={index}>{message}</li>
        ))}
      </ul>
    </div>
  );
}
